/**
 * アップロード直後の素材 (TemporaryMaterial) を選択中素材 (GenerationAsset) に変換する。
 *
 * 選択済み素材パネル / ホームカードへのファイル直接ドロップは、素材パネル経由
 * (SimpleMaterialsList → buildDragData) と違い `resolveMaterialUrls` を通らず、
 * `source_url || preview_url` のような素朴なフォールバックで sourceUrl を決めていた。
 * GCS 保存の動画・音声は `source_url` が無く `preview_url` がサムネイル JPEG を指すため、
 * 参照動画 (reference_videos) に画像 URL が流れて生成が失敗していた (2026-09-20 本番障害)。
 *
 * ここでは `resolveMaterialUrls` の source (動画・音声は署名 URL のみ許可) に一本化し、
 * GCS の動画・音声は署名付き URL を取得してから変換する。
 */
import type { GenerationAsset } from '../../components/generation/state/types';
import { fetchMaterialSignedUrl } from './api';
import type { TemporaryMaterial } from './materialsSlice';
import { isGcsMaterial, resolveMaterialUrls } from './materialUrl';
import { layerStackFromMaterial } from '../motion/layerStack';
import { ensureRasterAsset } from './ensureRasterAsset';
import { isNonRasterOriginal } from '../../components/materials/shared/materialUtils';

export function toUploadedGenerationAsset(
  material: TemporaryMaterial,
  gcsSignedUrl?: string | null,
): GenerationAsset {
  const urls = resolveMaterialUrls(material, gcsSignedUrl ?? null);
  const layerStack = layerStackFromMaterial(material);
  return {
    id: material.id,
    thumbnailUrl: urls.thumbnail || material.public_url || '',
    type: 'temporary',
    name: material.name ?? null,
    // 非ラスタ原本 (SVG) はラスタ版だけを sourceUrl にする (原本 URL は AI に流さない)。
    // 未生成なら undefined のまま返し、resolveUploadedGenerationAsset が取得する
    sourceUrl: (isNonRasterOriginal(material.content_type) ? urls.raster : urls.source) || undefined,
    storageBucket: material.storage_bucket,
    storagePath: material.storage_path,
    contentType: material.content_type,
    asin: material.asin,
    hasProductLinks: (material as { has_product_links?: boolean }).has_product_links ?? !!material.asin,
    width: material.width,
    height: material.height,
    // SVG を落とした直後のレイヤー素材 (応答は素材 1 件 + generation_metadata.layer_stack)
    ...(layerStack ? { layerStack } : {}),
  };
}

/**
 * GCS 保存の動画・音声は再生/参照用 URL が署名付き URL でしか得られないため、
 * 変換前に取得する。取得に失敗した場合は sourceUrl 無しで返し、addAsset 側の
 * 「読み込みが完了していません」ガードに委ねる (サムネイルを代用しない)。
 *
 * 非ラスタ原本 (SVG) はアップロード時に BE がラスタ版を同期生成するが、未生成 (失敗後の
 * 再アップロード等) ならここで取得する。失敗は投げる (呼び出し側が理由をトーストで出す)。
 */
export async function resolveUploadedGenerationAsset(material: TemporaryMaterial): Promise<GenerationAsset> {
  const contentType = material.content_type || '';
  const needsSigned = isGcsMaterial(material)
    && (contentType.startsWith('video/') || contentType.startsWith('audio/'));
  let signed: string | null = null;
  if (needsSigned) {
    try {
      signed = await fetchMaterialSignedUrl(material.id, true);
    } catch (error) {
      console.error('Failed to fetch signed URL for uploaded material:', error);
    }
  }
  return ensureRasterAsset(toUploadedGenerationAsset(material, signed));
}
